import React, { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction } from "@/hooks/useData";
import { Badge, Button, Field, Input, Modal, SectionCard, Switch } from "@/components/ui";

const NOTIFICATIONS = [
  "Leave applied", "Leave approved", "Leave rejected", "Monthly leave summary",
  "Salary slip generated", "Document expiry reminder", "Upcoming holiday alert",
];

const ROADMAP = [
  "WPS file (SIF) generation", "End-of-service gratuity", "Biometric device integration",
  "GPS attendance validation", "Multi-branch UAE setup", "Mobile app (iOS / Android)", "Visa renewal workflow",
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [prefs, setPrefs] = useState(() => Object.fromEntries(NOTIFICATIONS.map((n) => [n, true])));
  const [confirmReset, setConfirmReset] = useState(false);
  const { run, pending } = useAction();

  const reset = async () => {
    const { ok } = await run(() => api.demo.reset(), "Demo data reset");
    if (ok) window.location.reload();
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Company"
          action={isAdmin && <Button variant="primary" size="xs" icon={<Save size={12} />} onClick={() => toast.success("Company details saved")}>Save</Button>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Company name" className="sm:col-span-2"><Input defaultValue="MediLearn Tech Skills Training" disabled={!isAdmin} /></Field>
            <Field label="Address" className="sm:col-span-2"><Input defaultValue="Business Bay, Dubai, UAE" disabled={!isAdmin} /></Field>
            <Field label="HR email"><Input defaultValue="hr@medilearn.ae" disabled={!isAdmin} /></Field>
            <Field label="Currency"><Input value="AED" disabled /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Email Notifications" subtitle="Which events send an email">
          <div className="space-y-3">
            {NOTIFICATIONS.map((n) => (
              <div key={n} className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-slate-600">{n}</span>
                <Switch size="sm" checked={prefs[n]} onChange={(v) => setPrefs((p) => ({ ...p, [n]: v }))} label={n} />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Working Hours">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Office start"><Input type="time" defaultValue="09:00" disabled={!isAdmin} /></Field>
            <Field label="Office end"><Input type="time" defaultValue="18:00" disabled={!isAdmin} /></Field>
            <Field label="Ramadan start"><Input type="time" defaultValue="09:00" disabled={!isAdmin} /></Field>
            <Field label="Ramadan end"><Input type="time" defaultValue="15:00" disabled={!isAdmin} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Roadmap" subtitle="Planned integrations">
          <ul className="space-y-1">
            {ROADMAP.map((f) => (
              <li key={f} className="flex items-center justify-between gap-3 px-2 py-2 rounded-xl hover:shadow-neu-xs transition-all">
                <span className="text-xs font-semibold text-brand-blue">{f}</span>
                <Badge>Planned</Badge>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <SectionCard title="Demo Data" subtitle="All data lives in this browser's local storage">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-slate-500 max-w-xl">Leave requests, punches, payslips and certificates you create are saved locally. Resetting restores the original sample data for every account.</p>
          <Button variant="danger" icon={<RotateCcw size={14} />} onClick={() => setConfirmReset(true)}>Reset demo data</Button>
        </div>
      </SectionCard>

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset demo data?"
        description="Everything created in this browser will be replaced with the original sample data."
        footer={<>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button variant="destructive" loading={pending} onClick={reset}>Reset</Button>
        </>}
      />
    </>
  );
}
